import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Fish, Menu, X, ChevronDown, ExternalLink, LogIn } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

const menu = [
  { label: "Beranda", to: "/", slug: "beranda" },
  {
    label: "Bidang",
    slug: "bidang",
    sub: [
      { label: "Pembudidayaan, Pengolahan & Pemasaran", to: "/bidang/pembudidayaan-ikan-pengolahan-pemasaran" },
      { label: "Perikanan Tangkap", to: "/bidang/perikanan-tangkap" },
      { label: "Kelautan", to: "/bidang/kelautan" },
      { label: "Pengawasan Sumberdaya", to: "/bidang/pengawasan-sumberdaya-kelautan-perikanan" },
    ],
  },
  {
    label: "Aplikasi",
    slug: "aplikasi",
    sub: [
      { label: "SIPELIKAN", sublabel: "Pelaporan Mutu Pengolahan Ikan", href: "https://silaing.jabarprov.go.id/sipelikan/", badge: "New" },
    ],
  },
  {
    label: "Informasi",
    slug: "informasi",
    sub: [
      { label: "Berita", to: "/informasi/berita" },
      { label: "Regulasi", to: "/informasi/regulasi" },
      { label: "Galeri", to: "/informasi/galeri" },
      { label: "Agenda", to: "/informasi/agenda" },
      { label: "Statistik PILAHSAR", sublabel: "Dashboard Budidaya & Pengolahan", to: "/statistik/pilahsar", badge: "Live" },
      { label: "Statistik TANGKAP", sublabel: "Dashboard Perikanan Tangkap", to: "/statistik/tangkap", badge: "Live" },
      { label: "Statistik KELAUTAN", sublabel: "Dashboard Kelautan", to: "/statistik/kelautan", badge: "Live" },
      { label: "Brand Guideline", sublabel: "Logo & identitas visual", to: "/brand" },
    ],
  },
  { label: "Kontak", to: "/kontak", slug: "kontak" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const { pathname } = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setOpenSub(null); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#003A5C]/95 backdrop-blur-md shadow-lg" : "bg-[#003A5C]/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white grid place-items-center shadow-md p-1.5">
              <img src="/assets/silaing/logo-jabar.png" alt="Logo Pemerintah Provinsi Jawa Barat" className="w-full h-full object-contain" />
            </div>
            <div className="text-white">
              <div className="font-heading font-bold text-xl tracking-tight leading-none">SILAING</div>
              <div className="text-[10px] uppercase tracking-widest text-[#48CAE4] mt-1">DKP Jawa Barat</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {menu.map((item) => (
              <div key={item.slug} className="relative" onMouseEnter={() => setOpenSub(item.slug)} onMouseLeave={() => setOpenSub(null)}>
                {item.to ? (
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={NAV.menuItem(item.slug)}
                    className={({ isActive }) =>
                      `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "text-[#48CAE4]" : "text-white/90 hover:text-[#48CAE4]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ) : (
                  <button
                    data-testid={NAV.menuItem(item.slug)}
                    className="px-4 py-2 rounded-md text-sm font-medium text-white/90 hover:text-[#48CAE4] transition-colors inline-flex items-center gap-1"
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                )}
                {item.sub && openSub === item.slug && (
                  <div className="absolute top-full left-0 pt-2 min-w-[280px]">
                    <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-2 animate-float-up">
                      {item.sub.map((s, i) =>
                        s.href ? (
                          <a
                            key={i}
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F0F8FF] transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-[#003A5C] text-sm">{s.label}</span>
                                {s.badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00B4D8] text-white font-bold">{s.badge}</span>}
                              </div>
                              {s.sublabel && <div className="text-xs text-slate-500 mt-0.5">{s.sublabel}</div>}
                            </div>
                            <ExternalLink className="w-4 h-4 text-slate-400 mt-1" />
                          </a>
                        ) : (
                          <Link
                            key={i}
                            to={s.to}
                            className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F0F8FF] transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-[#003A5C] text-sm">{s.label}</span>
                                {s.badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00B4D8] text-white font-bold">{s.badge}</span>}
                              </div>
                              {s.sublabel && <div className="text-xs text-slate-500 mt-0.5">{s.sublabel}</div>}
                            </div>
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <Link
              to={user ? "/admin" : "/admin/login"}
              data-testid={NAV.adminLogin}
              className="ml-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00B4D8] hover:bg-[#48CAE4] text-[#003A5C] font-bold text-sm transition-colors"
            >
              <LogIn className="w-4 h-4" /> {user ? "Dashboard" : "Admin"}
            </Link>
          </nav>

          <button
            className="lg:hidden text-white p-2"
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#003A5C] border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {menu.map((item) =>
              item.sub ? (
                <details key={item.slug} className="group">
                  <summary className="list-none flex items-center justify-between px-3 py-2.5 rounded-lg text-white hover:bg-white/5 cursor-pointer font-medium">
                    {item.label}
                    <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="ml-3 mt-1 border-l-2 border-[#00B4D8]/40 pl-3 space-y-1">
                    {item.sub.map((s, i) =>
                      s.href ? (
                        <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-md text-white/80 text-sm hover:bg-white/5">
                          {s.label}
                        </a>
                      ) : (
                        <Link key={i} to={s.to} className="block px-3 py-2 rounded-md text-white/80 text-sm hover:bg-white/5">
                          {s.label}
                        </Link>
                      )
                    )}
                  </div>
                </details>
              ) : (
                <Link key={item.slug} to={item.to} className="block px-3 py-2.5 rounded-lg text-white hover:bg-white/5 font-medium">
                  {item.label}
                </Link>
              )
            )}
            <Link to={user ? "/admin" : "/admin/login"} className="block mt-3 px-3 py-2.5 rounded-lg bg-[#00B4D8] text-[#003A5C] font-bold text-center">
              {user ? "Dashboard Admin" : "Login Admin"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
